import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("alternai_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("alternai_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("alternai_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("alternai_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("alternai_token");
    setUser(null);
  };

  const upgrade = async () => {
    // Simple flow: POST → return checkout_url. Caller does window.location.
    // 5s timeout to avoid an "infinite" pending request.
    const { data } = await api.post(
      "/payments/checkout",
      {
        lookup_key: "alternai_pro_launch",
        origin_url: window.location.origin,
      },
      { timeout: 5000 }
    );
    if (!data?.checkout_url || !data.checkout_url.startsWith("https://checkout.stripe.com/")) {
      throw new Error("checkout_url invalide reçue du serveur");
    }
    return data.checkout_url;
  };

  const refreshProStatus = async () => {
    try {
      const { data } = await api.get("/auth/pro-status");
      setUser((u) => (u ? { ...u, is_pro: data.is_pro } : u));
      return data.is_pro;
    } catch {
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, upgrade, refresh: fetchMe, refreshProStatus }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
